<template>
  <div class="card-list-container">
    <HeaderBar 
          :user-power="userPower" 
          :is-show-back="false"
          header-title="内容生产工具"
          ref="headerBarRef"
        />
    <!-- 筛选按钮栏 -->
    <div class="filter-bar">
      <div 
        v-for="filter in filters" 
        :key="filter.value"
        class="filter-chip"
        :class="{ active: activeFilter === filter.value }"
        @click="setFilter(filter.value)"
      >
        {{ filter.label }}
      </div>
    </div>
    
    <!-- 工具卡片网格 -->
    <div class="card-list">
      <div
        v-for="(card, index) in filteredList"
        :key="card.id"
        class="tool-card"
        :class="getThumbClass(index)"
        @click="openCard(card)"
      >
      <div class="tool-thumb" :class="getThumbBgClass(index)">
        <video
          v-if="hasCardImage(card) && isVideoCover(card.img)"
          :ref="el => setVideoRef(el, card.id)"
          :src="card.img"
          class="tool-img"
          muted
          loop
          playsinline
          preload="metadata"
          :aria-label="`${card.title}封面预览`"
          @error="handleImageError(card)"
        />
        <img 
          v-else-if="hasCardImage(card)"
          :src="card.img" 
          class="tool-img" 
          alt="tool-image"
          @error="handleImageError(card)"
        />
        <el-icon v-else class="tool-icon" :size="42">
          <component :is="getToolIcon(card.type)" />
        </el-icon>
        <div class="tool-price-tag">
          <el-icon :size="12">
            <Coin />
          </el-icon>
          {{ card.power }}
        </div>
      </div>
      <div class="tool-info">
        <div class="tool-id">Tool-{{ card.code || `TOOL-${String(card.id).padStart(2, '0')}` }}</div>
        <div class="tool-title">{{ card.title }}</div>
        <div class="tool-desc">{{ card.desc }}</div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { getAiToolsList } from '@/api/portal/aitools'
import { ElMessage } from 'element-plus'
import { Coin, Document, VideoCamera, Picture, Mic, Box } from '@element-plus/icons-vue'
import HeaderBar from '@/apps/portal/components/HeaderBar.vue'

const cardList = ref([])
const activeFilter = ref('all')
const headerBarRef = ref(null)
const userPower = ref(0)
const router = useRouter()
const HIDDEN_TOOL_CODES = new Set([7, 10, 13, 14, 15, 63, 64])
const videoElements = new Map()
let videoObserver
let motionPreference

const isVideoCover = (value) => {
  try {
    return /\.(mp4|webm)$/i.test(new URL(value).pathname)
  } catch {
    return false
  }
}

const setVideoRef = (element, id) => {
  const previous = videoElements.get(id)
  if (previous) videoObserver?.unobserve(previous)
  if (element) {
    videoElements.set(id, element)
    videoObserver?.observe(element)
  } else {
    videoElements.delete(id)
  }
}

const updateVideoPlayback = () => {
  videoElements.forEach(video => {
    if (motionPreference?.matches || !video.dataset.visible) {
      video.pause()
    } else {
      video.play().catch(() => {})
    }
  })
}

// 筛选选项
const filters = [
  { label: '全部工具', value: 'all' },
  { label: '视频处理', value: 'video' },
  { label: '音频/语音', value: 'audio' },
  { label: '图片处理', value: 'img' },
  { label: '文本处理', value: 'txt' }
]

// 设置筛选
const setFilter = (value) => {
  activeFilter.value = value
}

// 筛选后的列表
const filteredList = computed(() => {
  if (activeFilter.value === 'all') {
    return cardList.value
  }
  return cardList.value.filter(card => card.type === activeFilter.value)
})

// 处理图片加载错误
const hasCardImage = (card) => {
  return Boolean(card.img && typeof card.img === 'string' && card.img.trim() && !card.imageFailed)
}

const handleImageError = (card) => {
  card.imageFailed = true
}

onMounted(async () => {
  motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)')
  motionPreference.addEventListener('change', updateVideoPlayback)
  videoObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      entry.target.dataset.visible = entry.isIntersecting ? 'true' : ''
    })
    updateVideoPlayback()
  }, { threshold: 0.1 })
  // 设置页面标题
  document.title = '大象魔法AIGC'
  
  // 获取用户信息
  const userInfo = localStorage.getItem('userInfo')
  if (userInfo) {
    const parsedInfo = JSON.parse(userInfo)
    userPower.value = parsedInfo.totalAmount || 0
  }

  try {
    const res = await getAiToolsList()
    if (res && Array.isArray(res.list)) {
      const typeMap = { 0: 'txt', 1: 'video', 2: 'img', 3: 'audio' }
      cardList.value = res.list
        .filter(item => !HIDDEN_TOOL_CODES.has(Number(item.code)))
        .map(item => ({
          id: item.code,
          code: item.code,
          img: item.img,
          imageFailed: false,
          type: typeMap[item.type] || 'video',
          power: item.power,
          title: item.title,
          desc: item.description
        }))
    }
  } catch (e) {
    // 可选：错误处理
    console.error('获取工具列表失败:', e)
    cardList.value = []
  }
})

onBeforeUnmount(() => {
  videoObserver?.disconnect()
  motionPreference?.removeEventListener('change', updateVideoPlayback)
  videoElements.forEach(video => video.pause())
  videoElements.clear()
})

const openCard = (card) => {
  // 检查登录状态
  const token = localStorage.getItem('token')
  if (!token) {
    ElMessage.warning(`请先登录后再使用  ${card.title}  功能`)
    return
  }
  
  // 已登录用户在新标签页打开
  let id = ''
  if (card.id < 10) {
    id = 'D0' + card.id
  } else {
    id = 'D' + card.id
  }
  if (Number(card.code) === 20) id = 'D20'
  const route = router.resolve({
    path: `/detail/${id}`,
    query: {
      code: card.code,
      power: card.power
    }
  })
  window.open(route.href, '_blank')
}

// 根据索引获取不同的背景类
const getThumbBgClass = (index) => {
  const classes = ['bg-blue', 'bg-purple', 'bg-orange', 'bg-dark']
  return classes[index % 4]
}

// 根据类型获取图标组件
const getToolIcon = (type) => {
  const iconMap = {
    'txt': Document,
    'video': VideoCamera,
    'img': Picture,
    'audio': Mic
  }
  return iconMap[type] || Box
}

// 获取缩略图类（用于特殊样式）
const getThumbClass = (index) => {
  return ''
}
</script>

<style scoped>
.card-list-container {
  width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  height: 100%;
  padding: 30px 40px;
}

/* 滚动条美化 */
.card-list-container::-webkit-scrollbar {
  width: 6px;
}
.card-list-container::-webkit-scrollbar-thumb {
  background: #E5E7EB;
  border-radius: 3px;
}

/* 筛选按钮栏 */
.filter-bar {
  margin-top: 20px;
  margin-bottom: 30px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-chip {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #E5E7EB;
  color: var(--text-secondary, #6B7280);
  background: #fff;
  user-select: none;
}

.filter-chip:hover {
  border-color: var(--accent-blue, #4F46E5);
  color: var(--accent-blue, #4F46E5);
}

.filter-chip.active {
  background: var(--text-primary, #1F2937);
  color: white;
  border-color: var(--text-primary, #1F2937);
}

/* 工具卡片网格 */
.card-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, 300px);
  gap: 24px;
  padding-bottom: 40px;
  justify-content: start;
}

/* 工具卡片 */
.tool-card {
  width: 300px;
  height: 260px;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.tool-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.06);
  border-color: var(--accent-blue, #4F46E5);
}

/* 卡片图片/缩略图区 */
.tool-thumb {
  height: 160px;
  width: 100%;
  background: #F3F4F6;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

/* 工具图片样式 */
.tool-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  display: block;
}

.tool-card:hover .tool-img {
  transform: scale(1.05);
}

/* 根据不同工具给不同的渐变底色 */
.bg-blue {
  background: linear-gradient(135deg, #E0E7FF 0%, #EEF2FF 100%);
}
.bg-purple {
  background: linear-gradient(135deg, #F3E8FF 0%, #FAF5FF 100%);
}
.bg-orange {
  background: linear-gradient(135deg, #FEF3C7 0%, #FFFBEB 100%);
}
.bg-dark {
  background: linear-gradient(135deg, #374151 0%, #1F2937 100%);
}

.tool-thumb .tool-icon {
  color: rgba(79, 70, 229, 0.2);
  transition: 0.3s;
}
.bg-dark .tool-icon {
  color: rgba(255, 255, 255, 0.2);
}

.tool-card:hover .tool-thumb .tool-icon {
  transform: scale(1.1);
  color: var(--accent-blue, #4F46E5);
}
.tool-card:hover .bg-dark .tool-icon {
  color: #fff;
}

/* 价格标签 */
.tool-price-tag {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.95);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  color: var(--el-color-warning);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 4px;
}

.tool-price-tag .el-icon {
  color: var(--el-color-warning);
}

/* 内容区 */
.tool-info {
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.tool-id {
  font-size: 11px;
  color: #9CA3AF;
  font-weight: 600;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.tool-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary, #1F2937);
  margin-bottom: 6px;
  line-height: 1.4;
}

.tool-desc {
  font-size: 13px;
  font-weight: 400;
  color: var(--text-secondary, #6B7280);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
